import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFixedPopoverPosition } from './useFixedPopover';

/**
 * Wraps a link's icon/arrow so its destination path (`to`) can be edited —
 * matches the EditablePicker visual pattern (pencil badge outside the icon,
 * portal-positioned popover so it can't get trapped under a later section's
 * scroll-reveal stacking context).
 */
export default function EditableLinkPath({ value, onChange, editable = false, label = 'Link', children }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const pos = useFixedPopoverPosition(triggerRef, open, 'above', popoverRef);

  const commit = () => {
    const next = draft.trim();
    if (next && next !== value) onChange(next);
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (popoverRef.current?.contains(e.target)) return;
      commit();
    };
    document.addEventListener('mousedown', onPointerDown, true);
    return () => document.removeEventListener('mousedown', onPointerDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft]);

  if (!editable) return children;

  const openPopover = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDraft(value || '');
    setOpen(true);
  };

  const popover = open && pos ? (
    <div
      ref={popoverRef}
      className="svcpg-popover"
      style={{
        position: 'fixed',
        left: pos.left,
        top: pos.top,
        transform: pos.flipUp ? 'translateY(-100%)' : undefined,
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <label className="svcpg-popover__field">
        <span>{label} destination</span>
        <input
          type="text"
          autoFocus
          value={draft}
          placeholder="/services/web-development"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') {
              setDraft(value || '');
              setOpen(false);
            }
          }}
        />
      </label>
      <div className="svcpg-popover__actions">
        <button
          type="button"
          className="svcpg-toolbar__btn"
          onClick={() => {
            setDraft(value || '');
            setOpen(false);
          }}
        >
          Cancel
        </button>
        <button type="button" className="svcpg-toolbar__btn svcpg-toolbar__btn--active" onClick={commit}>
          Apply
        </button>
      </div>
    </div>
  ) : null;

  return (
    <span className="svcpg-editable svcpg-editable-picker" ref={triggerRef}>
      <button
        type="button"
        className="svcpg-image-hit svcpg-image-hit--picker"
        onClick={openPopover}
        aria-label={`Edit ${label} destination`}
        title={value}
      >
        {children}
        <span className="svcpg-pencil svcpg-pencil--picker">✎</span>
      </button>
      {popover ? createPortal(popover, document.body) : null}
    </span>
  );
}
